import {
  CueHistory,
  CueHistoryActivityReview,
  CueHistoryReviewFilter,
  CueSyncActivity,
  CueTagSeverity,
  CueUserWarning,
  CueValidationError,
} from 'src/app/models/fincas/cue.model';
import { CueErrorPresenter } from './cue-error.presenter';

/** Extracts and formats CUE history records without leaking technical details in the main view. */
export class CueHistoryPresenter {
  public static historyCode(item: CueHistory): string {
    return item.codigo_peticion || item.codigo_comprovacion || '-';
  }

  public static historySummary(item: CueHistory): string {
    const activities = CueHistoryPresenter.getActivities(item);

    if (activities.length === 0) {
      return 'Sin detalle por actividad.';
    }

    return [
      'Actividades aceptadas: ' + CueHistoryPresenter.acceptedCount(item),
      'Actividades con problemas: ' + CueHistoryPresenter.rejectedCount(item),
      'Actividades aún por revisar: ' + CueHistoryPresenter.pendingCount(item)
    ].join('\n');
  }

  public static historyErrors(item: CueHistory): string {
    const issues = CueHistoryPresenter.actionableIssues(item);

    return issues.map((issue) => CueHistoryPresenter.issueAsText(issue)).join('\n\n');
  }

  public static technicalDetails(item: CueHistory): string {
    const activities = CueHistoryPresenter.getActivities(item);
    const cuadernoIds = Array.from(new Set(activities
      .map((activity) => activity.id_cuaderno ?? '')
      .filter((idCuaderno) => idCuaderno !== '')));
    const lines = [
      'Código petición: ' + CueHistoryPresenter.historyCode(item),
      'Estado técnico: ' + (item.estado || '-'),
      'ID histórico: ' + item.id,
      'Creado: ' + (item.created_at ?? '-'),
      'Procesado: ' + (item.processed_at ?? '-')
    ];

    if (cuadernoIds.length > 0) {
      lines.push('IdCuaderno: ' + cuadernoIds.join(', '));
    }

    return lines.join('\n');
  }

  public static userWarnings(item: CueHistory | null): CueUserWarning[] {
    if (!item) {
      return [];
    }

    return CueHistoryPresenter.actionableIssues(item);
  }

  public static actionableIssues(item: CueHistory): CueUserWarning[] {
    const rejectedWarnings = CueErrorPresenter.issuesFromRejectedActivities(CueHistoryPresenter.getActivities(item));

    if (rejectedWarnings.length > 0) {
      return rejectedWarnings;
    }

    return CueHistoryPresenter.getValidationErrors(item)
      .map((error, index) => CueErrorPresenter.warningFromValidationError(error, index));
  }

  public static activityReviews(item: CueHistory, filter: CueHistoryReviewFilter): CueHistoryActivityReview[] {
    return CueHistoryPresenter.getActivities(item)
      .filter((activity) => CueHistoryPresenter.activityMatchesFilter(activity, filter))
      .map((activity, index) => CueHistoryPresenter.activityReview(activity, index));
  }

  public static acceptedActivityLabels(item: CueHistory): string[] {
    return CueHistoryPresenter.activityReviews(item, 'accepted').map((activity) => activity.label);
  }

  public static acceptedCount(item: CueHistory): number {
    return CueHistoryPresenter.getActivities(item).filter((activity) => activity.es_valida === true).length;
  }

  public static rejectedCount(item: CueHistory): number {
    return CueHistoryPresenter.getActivities(item).filter((activity) => activity.es_valida === false).length;
  }

  public static pendingCount(item: CueHistory): number {
    const activities = CueHistoryPresenter.getActivities(item);

    return activities.length - CueHistoryPresenter.acceptedCount(item) - CueHistoryPresenter.rejectedCount(item);
  }

  public static getActivities(item: CueHistory): CueSyncActivity[] {
    if (Array.isArray(item.activity_summary_json)) {
      return item.activity_summary_json;
    }

    if (typeof item.activity_summary_json !== 'string') {
      return [];
    }

    try {
      const parsedValue = JSON.parse(item.activity_summary_json) as unknown;

      return Array.isArray(parsedValue) ? (parsedValue as CueSyncActivity[]) : [];
    } catch {
      return [];
    }
  }

  public static getValidationErrors(item: CueHistory): CueValidationError[] {
    if (Array.isArray(item.validation_errors_json)) {
      return item.validation_errors_json;
    }

    if (typeof item.validation_errors_json !== 'string') {
      return [];
    }

    try {
      const parsedValue = JSON.parse(item.validation_errors_json) as unknown;

      return Array.isArray(parsedValue) ? (parsedValue as CueValidationError[]) : [];
    } catch {
      return [];
    }
  }

  private static activityMatchesFilter(activity: CueSyncActivity, filter: CueHistoryReviewFilter): boolean {
    if (filter === 'accepted') {
      return activity.es_valida === true;
    }

    if (filter === 'problems') {
      return activity.es_valida === false;
    }

    return true;
  }

  private static activityReview(activity: CueSyncActivity, index: number): CueHistoryActivityReview {
    const isAccepted = activity.es_valida === true;
    const hasProblem = activity.es_valida === false;
    let severity: CueTagSeverity = 'info';
    let statusLabel = 'Pendiente de revisar';

    if (isAccepted) {
      severity = 'success';
      statusLabel = 'Aceptada';
    }

    if (hasProblem) {
      severity = 'warn';
      statusLabel = 'Con problemas';
    }

    return {
      id: 'activity-review-' + (activity.activity_type ?? 'activity') + '-' + (activity.local_activity_id ?? index),
      label: CueErrorPresenter.activityUserLabel(activity),
      severity,
      statusLabel,
      technicalDetail: CueHistoryPresenter.activityTechnicalDetail(activity)
    };
  }

  private static activityTechnicalDetail(activity: CueSyncActivity): string {
    return [
      'Tipo actividad: ' + (activity.activity_type ?? '-'),
      'ID local: ' + (activity.local_activity_id ?? '-'),
      'ID ajena: ' + (activity.id_ajena ?? '-'),
      'IdCuaderno: ' + (activity.id_cuaderno ?? '-'),
      'Código remoto: ' + (activity.remote_error_code ?? '-'),
      'Mensaje remoto: ' + (activity.remote_error_message ?? '-')
    ].join('\n');
  }

  private static issueAsText(issue: CueUserWarning): string {
    return [
      'Actividad con problema: ' + issue.title,
      'Motivo: ' + issue.message,
      'Dónde corregirlo: ' + issue.location,
      'Acción: ' + issue.action
    ].join('\n');
  }
}
